import { motion } from 'framer-motion'

/**
 * Compliance Component
 * Global compliance and regulatory expertise section with modern design
 */
const Compliance = () => {
  const regulations = [
    {
      name: 'FDA (U.S.)',
      details: 'Drug Facts panels, NDC formatting, tamper-evident labeling, and OTC monograph alignment',
      icon: '🇺🇸',
    },
    {
      name: 'CDSCO (India)',
      details: 'Schedule M, labeling norms, and regional language mandates',
      icon: '🇮🇳',
    },
    {
      name: 'EU Regulations',
      details: 'QRD templates, pharmacovigilance statements, CE markings, and multilingual leaflet formatting',
      icon: '🇪🇺',
    },
    {
      name: 'GCC Guidelines',
      details: 'Arabic labeling, SFDA compliance, and bilingual packaging for Saudi Arabia, UAE, and beyond',
      icon: '🇸🇦',
    },
  ]

  const localization = [
    {
      title: 'Arabic packaging',
      description: 'Right-to-left formatting, typographic alignment, and cultural sensitivity',
    },
    {
      title: 'Multilingual layouts',
      description: 'Seamless integration of 2–5 languages per pack, preserving hierarchy and legibility',
    },
    {
      title: 'Translation coordination',
      description: 'Working with your language partners or ours to ensure accuracy and layout fit',
    },
    {
      title: 'Cultural adaptation',
      description: 'Iconography, color usage, and claims tailored to local norms and consumer expectations',
    },
    {
      title: 'Regulatory alignment',
      description: 'Ensuring translations meet local labeling laws and do not compromise compliance',
    },
  ]

  return (
    <section
      id="compliance"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white relative overflow-hidden border-t-4 border-primary-600/50"
      aria-labelledby="compliance-heading"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            id="compliance-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
          >
            Compliance – Built for Global Standards
          </h2>
          <p className="text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto">
            Precision Isn't Optional. It's Our Standard.
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <p className="text-lg md:text-xl text-primary-50 leading-relaxed text-center">
            At Artwork Solution, compliance is not an afterthought — it's the foundation of every packaging artwork we create. With deep expertise in global regulatory frameworks and multilingual localization, we ensure your packaging is not only visually compelling but also legally sound and market-ready.
          </p>
        </motion.div>

        {/* Global Regulatory Expertise */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            Global Regulatory Expertise
          </h3>
          <p className="text-primary-50 mb-8 text-center max-w-3xl mx-auto">
            We navigate complex compliance landscapes so you don't have to. Our team is fluent in the language of:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regulations.map((regulation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                <div className="text-4xl mb-3">{regulation.icon}</div>
                <h4 className="font-bold text-lg mb-2">{regulation.name}</h4>
                <p className="text-primary-100 text-sm leading-relaxed">{regulation.details}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-primary-50 mt-8 text-center max-w-3xl mx-auto">
            We stay ahead of evolving standards, ensuring your packaging meets the latest legal, safety, and accessibility requirements — from Braille integration to nutritional declarations and barcode placement.
          </p>
        </motion.div>

        {/* Multilingual & Cultural Localization */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            Multilingual & Cultural Localization
          </h3>
          <p className="text-primary-50 mb-8 text-center max-w-3xl mx-auto">
            Your packaging should speak every language your market does — clearly, correctly, and culturally. We specialize in:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {localization.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                <h4 className="font-bold text-lg mb-2 text-white">{item.title}:</h4>
                <p className="text-primary-100 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Bottom decorative divider */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-transparent via-white/5 to-white/10"></div>
    </section>
  )
}

export default Compliance
